import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { deliveryApi } from '../api/delivery';
import {
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  ShoppingBag,
  ChevronLeft,
  Loader2,
} from 'lucide-react';

// ─── Status config ────────────────────────────────────────────────────────────

type StatusKey =
  | 'PENDING'
  | 'ACCEPTED'
  | 'IN_TRANSIT'
  | 'DELIVERED'
  | 'REJECTED'
  | 'CANCELLED';

const STATUS_CONFIG: Record<
  StatusKey,
  { label: string; icon: React.ReactNode; color: string; bg: string; description: string }
> = {
  PENDING: {
    label: 'Aguardando confirmação',
    icon: <Clock className="w-8 h-8" />,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    description: 'Seu pedido foi recebido e está aguardando a loja confirmar.',
  },
  ACCEPTED: {
    label: 'Pedido confirmado',
    icon: <CheckCircle className="w-8 h-8" />,
    color: 'text-green-600',
    bg: 'bg-green-50',
    description: 'A loja confirmou seu pedido! Estamos preparando tudo para você.',
  },
  IN_TRANSIT: {
    label: 'Saiu para entrega',
    icon: <Truck className="w-8 h-8" />,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    description: 'Seu pedido está a caminho! O entregador já saiu.',
  },
  DELIVERED: {
    label: 'Entregue',
    icon: <CheckCircle className="w-8 h-8" />,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    description: 'Pedido entregue com sucesso. Bom apetite!',
  },
  REJECTED: {
    label: 'Pedido recusado',
    icon: <XCircle className="w-8 h-8" />,
    color: 'text-red-600',
    bg: 'bg-red-50',
    description: 'Infelizmente a loja não pôde aceitar seu pedido no momento.',
  },
  CANCELLED: {
    label: 'Pedido cancelado',
    icon: <XCircle className="w-8 h-8" />,
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    description: 'Este pedido foi cancelado.',
  },
};

const STEP_ORDER: StatusKey[] = ['PENDING', 'ACCEPTED', 'IN_TRANSIT', 'DELIVERED'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCents(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// ─── Components ──────────────────────────────────────────────────────────────

function StatusStepper({ current }: { current: StatusKey }) {
  const isFinal = current === 'REJECTED' || current === 'CANCELLED';
  if (isFinal) return null;

  const currentIdx = STEP_ORDER.indexOf(current);

  return (
    <div className="flex items-center gap-0 mb-6">
      {STEP_ORDER.map((step, idx) => {
        const done = idx <= currentIdx;
        const isLast = idx === STEP_ORDER.length - 1;
        return (
          <div key={step} className="flex items-center flex-1 last:flex-none">
            {/* dot */}
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 transition-colors ${
                done ? 'bg-orange-500' : 'bg-gray-200'
              }`}
            />
            {/* line */}
            {!isLast && (
              <div
                className={`flex-1 h-1 transition-colors ${
                  idx < currentIdx ? 'bg-orange-500' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function OrderStatusPage() {
  const { slug: tenantSlug, orderId } = useParams<{ slug: string; orderId: string }>();
  const navigate = useNavigate();

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order', tenantSlug, orderId],
    queryFn: () => deliveryApi.getOrderStatus(tenantSlug!, orderId!),
    refetchInterval: (query) => {
      const status = query.state.data?.deliveryStatus as StatusKey | undefined;
      if (!status) return 5000;
      if (status === 'DELIVERED' || status === 'REJECTED' || status === 'CANCELLED') return false;
      return 5000;
    },
    enabled: !!tenantSlug && !!orderId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center">
        <ShoppingBag className="w-12 h-12 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">Não foi possível carregar o pedido.</p>
        <button
          onClick={() => navigate(`/${tenantSlug}`)}
          className="text-orange-500 font-semibold text-sm"
        >
          Voltar à loja
        </button>
      </div>
    );
  }

  const statusKey = (order.deliveryStatus as StatusKey) in STATUS_CONFIG
    ? (order.deliveryStatus as StatusKey)
    : 'PENDING';
  const cfg = STATUS_CONFIG[statusKey];

  const subtotalCents = order.totalCents - order.deliveryFeeCents;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/${tenantSlug}`)} className="text-gray-400 hover:text-gray-600">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold text-gray-800">Acompanhar pedido</h1>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Status card */}
        <div className={`rounded-2xl p-5 ${cfg.bg}`}>
          <div className={`flex items-center gap-3 mb-3 ${cfg.color}`}>
            {cfg.icon}
            <span className="font-bold text-lg">{cfg.label}</span>
          </div>

          <StatusStepper current={statusKey} />

          <p className="text-sm text-gray-600">{cfg.description}</p>

          {/* Estimated time — show while in progress */}
          {order.deliveryZone?.estimatedMin && statusKey !== 'DELIVERED' && statusKey !== 'REJECTED' && statusKey !== 'CANCELLED' && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Tempo estimado: ~{order.deliveryZone.estimatedMin} min</span>
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="bg-white rounded-2xl p-5 space-y-3">
          <h2 className="font-semibold text-gray-800">Resumo do pedido</h2>
          <p className="text-sm text-gray-500">Olá, {order.customerName}</p>

          <div className="divide-y divide-gray-50">
            {order.items.map((item, idx) => (
              <div key={idx} className="py-2 flex justify-between text-sm">
                <span className="text-gray-700">
                  {item.quantity}x {item.product.name}
                </span>
                <span className="text-gray-500 font-medium">
                  {formatCents(item.quantity * item.unitPriceCents)}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-gray-500">
              <span>Subtotal</span>
              <span>{formatCents(subtotalCents)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>Entrega</span>
              <span>
                {order.deliveryFeeCents === 0 ? (
                  <span className="text-green-600 font-medium">Grátis</span>
                ) : (
                  formatCents(order.deliveryFeeCents)
                )}
              </span>
            </div>
            <div className="flex justify-between font-bold text-gray-800 pt-1">
              <span>Total</span>
              <span>{formatCents(order.totalCents)}</span>
            </div>
          </div>

          <p className="text-xs text-gray-400">Pagamento na entrega</p>
        </div>

        {/* Order ID */}
        <p className="text-center text-xs text-gray-400">
          Pedido #{orderId?.slice(0, 8).toUpperCase()}
        </p>
      </div>
    </div>
  );
}
